import React, { useState } from "react";
import { 
  FiCheckCircle, 
  FiArrowLeft, 
  FiArrowRight, 
  FiStar, 
  FiHelpCircle,
  FiZap,
  FiLayers,
  FiActivity,
  FiBarChart2,
  FiShield,
  FiGitBranch,
  FiEye
} from "react-icons/fi";
import type { RcaMethodCode } from "../../../types/rca.types.js";

export type RcaMethodSelectionWizardProps = {
  onBack: () => void;
  onConfirmMethod: (methodCode: RcaMethodCode, methodName: string) => void;
};

type ProblemTypeKey = 
  | "SIMPLE_LINEAR"
  | "MULTI_FACTOR"
  | "SYSTEM_LOGIC"
  | "CUSTOMER_FORMAL"
  | "PROCESS_FLOW"
  | "UNCLEAR_BOUNDARY"
  | "PRIORITIZATION"
  | "DIVERGENT_IDEAS";

type MethodMeta = {
  code: RcaMethodCode;
  name: string;
  shortDesc: string;
  icon: string;
  tags: string[];
  bestFor: string;
};

const METHODS_CATALOG: Record<RcaMethodCode, MethodMeta> = {
  FIVE_WHY: {
    code: "FIVE_WHY",
    name: "5-Why Analysis",
    shortDesc: "Best for simple problems with likely linear causation.",
    icon: "5W",
    tags: ["Easy to use", "Fast execution", "Good for process issues"],
    bestFor: "Simple/well-scoped issues with single sequential causal path."
  },
  FISHBONE: {
    code: "FISHBONE",
    name: "Fishbone Diagram (Ishikawa)",
    shortDesc: "Categorize multi-factor hypotheses across 6M dimensions.",
    icon: "FB",
    tags: ["Cross-functional", "6M Categories", "Team Voting"],
    bestFor: "Broad cause exploration when multiple failure origins are plausible."
  },
  FTA: {
    code: "FTA",
    name: "Fault Tree Analysis (FTA)",
    shortDesc: "Deductive top-down Boolean logic gates (AND/OR).",
    icon: "FTA",
    tags: ["High Severity", "Safety Critical", "Quantitative Probability"],
    bestFor: "Complex system failures, electronics, or safety-critical defects."
  },
  PARETO: {
    code: "PARETO",
    name: "Pareto Analysis (80/20)",
    shortDesc: "Isolate vital few defect drivers before deeper investigation.",
    icon: "80/20",
    tags: ["Statistical", "Prioritization", "Cumulative %"],
    bestFor: "Multiple concurrent defect codes or high-frequency recurring issues."
  },
  "8D": {
    code: "8D",
    name: "8D Problem Solving",
    shortDesc: "Formal automotive/aerospace 8-Discipline structured method.",
    icon: "8D",
    tags: ["Automotive / OEM", "Containment (D3)", "Systemic Recurrence"],
    bestFor: "OEM customer complaints requiring formal D0–D8 containment and closure."
  },
  PROCESS_MAP: {
    code: "PROCESS_MAP",
    name: "Process Mapping & Value Stream",
    shortDesc: "Step-by-step flowchart identifying failure points and rework loops.",
    icon: "MAP",
    tags: ["Flowchart", "Cycle Time", "Failure Points"],
    bestFor: "Manufacturing assembly line defects and process transition handoffs."
  },
  IS_IS_NOT: {
    code: "IS_IS_NOT",
    name: "Is / Is Not Analysis (Kepner-Tregoe)",
    shortDesc: "4-Dimensional boundary matrix (What, Where, When, Extent).",
    icon: "IS",
    tags: ["Boundary Setting", "Hypothesis Testing", "Contrast Logic"],
    bestFor: "Intermittent or elusive problems where boundary contrast isolates root cause."
  },
  BRAINSTORMING: {
    code: "BRAINSTORMING",
    name: "Brainstorming & Affinity Clustering",
    shortDesc: "Collaborative idea board with sticky notes and multi-voting.",
    icon: "IDEA",
    tags: ["Divergent Thinking", "Idea Clustering", "Voting"],
    bestFor: "Novel failure modes requiring out-of-the-box team ideation."
  },
  FMEA_REF: {
    code: "FMEA_REF",
    name: "FMEA Reference",
    shortDesc: "PFMEA / DFMEA risk document linkage.",
    icon: "FMEA",
    tags: ["Risk", "AP Scoring"],
    bestFor: "Risk register updating."
  }
};

const RECOMMENDATION_MAP: Record<ProblemTypeKey, RcaMethodCode> = {
  SIMPLE_LINEAR: "FIVE_WHY",
  MULTI_FACTOR: "FISHBONE",
  SYSTEM_LOGIC: "FTA",
  CUSTOMER_FORMAL: "8D",
  PROCESS_FLOW: "PROCESS_MAP",
  UNCLEAR_BOUNDARY: "IS_IS_NOT",
  PRIORITIZATION: "PARETO",
  DIVERGENT_IDEAS: "BRAINSTORMING",
};

export function RcaMethodSelectionWizard({
  onBack,
  onConfirmMethod,
}: RcaMethodSelectionWizardProps) {
  const [selectedProblemType, setSelectedProblemType] = useState<ProblemTypeKey>("SIMPLE_LINEAR");
  const [selectedMethod, setSelectedMethod] = useState<RcaMethodCode>("FIVE_WHY");

  const handleProblemTypeChange = (key: ProblemTypeKey) => {
    setSelectedProblemType(key);
    const recommended = RECOMMENDATION_MAP[key];
    setSelectedMethod(recommended);
  };

  const currentMethodMeta = METHODS_CATALOG[selectedMethod] || METHODS_CATALOG.FIVE_WHY;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-6">
      {/* Header */}
      <div className="p-6 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Step 2 of 2: Method Recommendation Engine</div>
          <h2 className="text-xl font-bold text-slate-900 mt-0.5">Select RCA Method Workspace</h2>
          <p className="text-xs text-slate-500 mt-1">
            Answer the problem type diagnostic below to receive intelligent method recommendations, or choose directly.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-lg flex items-center gap-1.5 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            onClick={() => onConfirmMethod(selectedMethod, currentMethodMeta.name)}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
          >
            Confirm & Open {currentMethodMeta.name} <FiArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Grid: Problem Questionnaire on Left + Method Cards on Right */}
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Problem Type Questionnaire */}
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Diagnostic Questionnaire: What type of problem are you investigating?
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Select the condition that best matches your observation:
            </p>
          </div>

          <div className="space-y-2.5">
            {[
              { key: "SIMPLE_LINEAR", label: "Simple linear cause-and-effect chain", hint: "Single failure path, clear sequence of events" },
              { key: "MULTI_FACTOR", label: "Multiple potential contributing causes (Man, Machine, Method...)", hint: "Needs broad exploration across 6M categories and team brainstorming" },
              { key: "SYSTEM_LOGIC", label: "Complex electronics, software, or safety-critical failure", hint: "Multiple redundant paths, Boolean logic gates (AND/OR), quantitative fault probabilities" },
              { key: "CUSTOMER_FORMAL", label: "Formal customer 8D required (Automotive / Aerospace)", hint: "Requires immediate containment (D3), root cause verification (D4), and systemic validation (D7)" },
              { key: "PROCESS_FLOW", label: "Manufacturing line or workflow transition failure", hint: "Defect originates across multiple line handoffs, stations, or rework loops" },
              { key: "UNCLEAR_BOUNDARY", label: "Problem boundary unclear (What/Where/When)", hint: "Need to separate what is happening from what is NOT happening" },
              { key: "PRIORITIZATION", label: "Prioritization needed across defect categories", hint: "Many defect types; need 80/20 Pareto distribution before deeper RCA" },
              { key: "DIVERGENT_IDEAS", label: "Initial hypothesis generation & team voting", hint: "Need collaborative brainstorming and sticky note clustering" },
            ].map(item => (
              <label
                key={item.key}
                onClick={() => handleProblemTypeChange(item.key as ProblemTypeKey)}
                className={`p-3 rounded-lg border flex items-start gap-3 cursor-pointer transition-all ${
                  selectedProblemType === item.key
                    ? "bg-blue-50 border-blue-500 shadow-sm"
                    : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  name="problemType"
                  checked={selectedProblemType === item.key}
                  onChange={() => handleProblemTypeChange(item.key as ProblemTypeKey)}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                />
                <div className="space-y-0.5">
                  <div className="text-xs font-bold text-slate-900">{item.label}</div>
                  <div className="text-[11px] text-slate-500">{item.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Right Column: Recommended Method Card + Other Methods Selector */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
            Recommended Method
          </h3>

          {/* Primary Recommendation Card */}
          <div className="p-5 rounded-xl bg-blue-50/70 border-2 border-blue-400 shadow-sm space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-600 text-white font-bold text-base flex items-center justify-center shadow-sm">
                  {currentMethodMeta.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-bold text-slate-900">{currentMethodMeta.name}</h4>
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold text-[10px] flex items-center gap-0.5">
                      <FiStar className="w-3 h-3 text-amber-500 fill-amber-500" /> Best Match
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 font-medium mt-0.5">{currentMethodMeta.shortDesc}</p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-white rounded-lg border border-blue-200 space-y-2">
              <div className="text-[11px] text-slate-500 font-semibold uppercase">Why this method:</div>
              <p className="text-xs text-slate-800 leading-relaxed font-medium">
                {currentMethodMeta.bestFor}
              </p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {currentMethodMeta.tags.map(t => (
                  <span key={t} className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-semibold">
                    ✓ {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Other Methods Grid */}
          <div className="space-y-2 pt-2">
            <h4 className="text-xs font-bold text-slate-700">
              Or Choose Another Method:
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(Object.keys(METHODS_CATALOG) as RcaMethodCode[]).filter(m => m !== "FMEA_REF").map(methodKey => {
                const meta = METHODS_CATALOG[methodKey];
                const isSelected = selectedMethod === methodKey;
                return (
                  <button
                    key={methodKey}
                    type="button"
                    onClick={() => setSelectedMethod(methodKey)}
                    className={`p-2.5 rounded-lg border text-center transition-all ${
                      isSelected
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm font-bold scale-105"
                        : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
                    }`}
                  >
                    <div className="text-xs font-bold truncate">{meta.name.split(" ")[0]}</div>
                    <div className={`text-[10px] truncate ${isSelected ? "text-blue-100" : "text-slate-400"}`}>{meta.icon}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Selected: <strong className="text-slate-900">{currentMethodMeta.name}</strong>
        </div>
        <button
          type="button"
          onClick={() => onConfirmMethod(selectedMethod, currentMethodMeta.name)}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5"
        >
          Launch Method Workspace <FiArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
